import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBranding } from '../../contexts/BrandingContext';
import notificationService from '../../services/notificationService';

interface MenuItem {
  path: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
}

interface MenuGroup {
  title?: string;
  items: MenuItem[];
}

interface SideNavProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
}

// Icon components using SVG
const Icons = {
  dashboard: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  ),
  aiSearch: (
    // Sparkles — used for the AI Search nav item.
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L23 12l-6.714 2.143L14 21l-2.286-6.857L5 12l6.714-2.143L14 3z" />
    </svg>
  ),
  transactions: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  plus: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  projects: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
    </svg>
  ),
  mom: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  approval: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  users: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  notifications: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  reports: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  assignments: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  ),
  email: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  audit: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  settings: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  tasks: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  upload: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  ),
  drafts: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  document: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  template: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
    </svg>
  ),
  profile: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  payment: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  palette: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
    </svg>
  ),
  kb: (
    // Library / collection — Knowledge Base nav item.
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  ),
};

const EXPANDED_STORAGE_KEY = 'sidenav-expanded';

// Stable, language-independent group id derived from the group's first
// item path (titles are localized, so they can't key persistence).
const groupKey = (group: MenuGroup): string =>
  group.items[0]?.path.split('/')[1] || 'misc';

const SideNav: React.FC<SideNavProps> = ({ collapsed = false, onCollapse }) => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { branding } = useBranding();
  const location = useLocation();
  const [notificationCount, setNotificationCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(EXPANDED_STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch { /* corrupted state — fall through to default */ }
    return { dashboard: true };
  });

  // Accordion: opening a group closes whatever was open, so the sidebar never
  // grows into a wall of every section at once. The group holding the current
  // route stays visible regardless — see isGroupOpen — so collapsing the rest
  // never hides where you are.
  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const opening = !isGroupOpen(key);
      const next = opening ? { [key]: true } : { ...prev, [key]: false };
      try { localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  };

  useEffect(() => {
    const loadNotificationCount = async () => {
      try {
        const count = await notificationService.getUnreadCount();
        setNotificationCount(count);
      } catch (error) {
        console.error('Error loading notification count:', error);
      }
    };
    loadNotificationCount();
  }, []);

  const getMenuGroups = (): MenuGroup[] => {
    const role = user?.role?.toLowerCase();

    if (role === 'admin') {
      return [
        {
          title: t('main') || 'Main',
          items: [
            { path: '/dashboard', icon: Icons.dashboard, label: t('dashboard') },
            { path: '/ai-search', icon: Icons.aiSearch, label: language === 'ar' ? 'البحث الذكي' : 'AI Search' },
            { path: '/kb', icon: Icons.kb, label: language === 'ar' ? 'قاعدة المعرفة' : 'Knowledge Base' },
          ],
        },
        ...(user?.can_access_applications ? [{
          title: t('applications') || 'Applications',
          items: [
            { path: '/transactions', icon: Icons.transactions, label: t('allTransactions') },
            { path: '/transactions/create', icon: Icons.plus, label: t('createTransaction') },
          ],
        }] : []),
        ...(user?.can_access_projects ? [{
          title: t('projects') || 'Projects',
          items: [
            { path: '/projects', icon: Icons.projects, label: t('myProjects') },
            { path: '/projects/new', icon: Icons.plus, label: t('createProject') },
            { path: '/claims', icon: Icons.approval, label: language === 'ar' ? 'المستخلصات والطلبات' : 'Claims & Requests' },
          ],
        }] : []),
        ...(user?.can_access_mom ? [{
          title: language === 'ar' ? 'محاضر الاجتماعات' : 'Minutes of Meeting',
          items: [
            { path: '/moms', icon: Icons.mom, label: language === 'ar' ? 'محاضر اجتماعاتي' : 'My MOMs' },
            { path: '/meetings', icon: Icons.mom, label: language === 'ar' ? 'الاجتماعات' : 'Meetings' },
            { path: '/moms/new', icon: Icons.plus, label: language === 'ar' ? 'إنشاء محضر اجتماع' : 'Create MOM' },
            { path: '/moms/approvals', icon: Icons.approval, label: language === 'ar' ? 'موافقات محاضر الاجتماعات' : 'MOM Approvals' },
          ],
        }] : []),
        ...(user?.can_access_documents ? [{
          title: language === 'ar' ? 'المستندات' : 'Documents',
          items: [
            { path: '/documents', icon: Icons.document, label: language === 'ar' ? 'المستندات' : 'My Documents' },
            { path: '/documents/create', icon: Icons.plus, label: language === 'ar' ? 'إنشاء مستند' : 'Create Document' },
            { path: '/documents/templates', icon: Icons.template, label: language === 'ar' ? 'قوالب المستندات' : 'Document Templates' },
            { path: '/documents/templates/create', icon: Icons.plus, label: language === 'ar' ? 'إنشاء قالب مستند' : 'Create Doc Template' },
          ],
        }] : []),
        ...(user?.can_access_proposals ? [{
          title: language === 'ar' ? 'العروض' : 'Proposals',
          items: [
            { path: '/proposals', icon: Icons.document, label: language === 'ar' ? 'عروض الأسعار' : 'Proposals' },
            { path: '/proposals/new', icon: Icons.plus, label: language === 'ar' ? 'إنشاء عرض' : 'Create Proposal' },
            { path: '/proposals/templates', icon: Icons.template, label: language === 'ar' ? 'قوالب العروض' : 'Proposal Templates' },
            { path: '/proposals/designs', icon: Icons.palette, label: language === 'ar' ? 'قوالب التصميم' : 'Design Templates' },
          ],
        }] : []),
        ...(user?.can_access_payments ? [{
          title: language === 'ar' ? 'المدفوعات' : 'Payments',
          items: [
            { path: '/payments', icon: Icons.payment, label: language === 'ar' ? 'المدفوعات' : 'Payments' },
            // PR 5 — verification queue (admin only). Editors with
            // can_access_payments record manual payments but cannot verify.
            ...(user?.can_verify_payments ? [{
              path: '/payments/pending-verification',
              icon: Icons.approval,
              label: language === 'ar' ? 'بانتظار التحقق' : 'Pending Verification',
            }] : []),
          ],
        }] : []),
        ...(user?.can_access_invoicing ? [{
          title: language === 'ar' ? 'الفواتير' : 'Invoicing',
          items: [
            { path: '/invoices', icon: Icons.document, label: language === 'ar' ? 'الفواتير' : 'Invoices' },
            { path: '/invoices/new', icon: Icons.plus, label: language === 'ar' ? 'فاتورة جديدة' : 'New Invoice' },
            // Invoice settings live under System Settings now — see
            // components/settings/InvoicingSettingsTab. Sidebar entry
            // removed to avoid two paths to the same form.
          ],
        }] : []),
        ...(user?.can_access_expenses ? [{
          title: language === 'ar' ? 'المصروفات' : 'Expenses',
          items: [
            { path: '/expenses', icon: Icons.payment, label: language === 'ar' ? 'المصروفات' : 'Expenses' },
            { path: '/expenses/new', icon: Icons.plus, label: language === 'ar' ? 'مصروف جديد' : 'New Expense' },
            // PR 8 — AI Import. Plan-gated; only Pro+ users see it.
            ...(user?.can_use_ai_expense_import ? [{
              path: '/expenses/import',
              icon: Icons.upload,
              label: language === 'ar' ? 'استيراد ذكي' : 'AI Import',
            }] : []),
          ],
        }] : []),
        ...(user?.can_access_income ? [{
          title: language === 'ar' ? 'الإيرادات' : 'Income',
          items: [
            { path: '/income', icon: Icons.payment, label: language === 'ar' ? 'الإيرادات' : 'Income' },
            { path: '/income/new', icon: Icons.plus, label: language === 'ar' ? 'إيراد جديد' : 'New Income' },
          ],
        }] : []),
        ...(user?.can_access_financial_reports ? [{
          title: language === 'ar' ? 'البيانات المالية' : 'Financials',
          items: [
            { path: '/financials', icon: Icons.reports, label: language === 'ar' ? 'لوحة البيانات المالية' : 'Dashboard' },
          ],
        }] : []),
        {
          title: t('administration') || 'Administration',
          items: [
            { path: '/users', icon: Icons.users, label: t('userManagement') },
            { path: '/notifications', icon: Icons.notifications, label: t('notifications'), badge: notificationCount > 0 ? notificationCount : undefined },
            { path: '/reports', icon: Icons.reports, label: t('reportsAnalytics') },
            { path: '/assignments', icon: Icons.assignments, label: t('assignments') },
            { path: '/email-templates', icon: Icons.email, label: t('emailTemplates') },
            { path: '/email-log', icon: Icons.audit, label: language === 'ar' ? 'سجل الإيميلات' : 'Email log' },
            { path: '/audit-logs', icon: Icons.audit, label: t('auditLogs') },
            { path: '/settings', icon: Icons.settings, label: t('systemSettings') },
          ],
        },
      ];
    } else if (role === 'editor') {
      return [
        {
          title: t('main') || 'Main',
          items: [
            { path: '/dashboard', icon: Icons.dashboard, label: t('dashboard') },
            { path: '/ai-search', icon: Icons.aiSearch, label: language === 'ar' ? 'البحث الذكي' : 'AI Search' },
            { path: '/kb', icon: Icons.kb, label: language === 'ar' ? 'قاعدة المعرفة' : 'Knowledge Base' },
          ],
        },
        ...(user?.can_access_applications ? [{
          title: t('applications') || 'Applications',
          items: [
            { path: '/transactions', icon: Icons.transactions, label: t('myTransactions') },
            { path: '/transactions/create', icon: Icons.plus, label: t('createTransaction') },
            { path: '/assigned-tasks', icon: Icons.tasks, label: t('assignedTransactions') },
            { path: '/import-wizard', icon: Icons.upload, label: t('bulkImport') },
            { path: '/drafts', icon: Icons.drafts, label: t('drafts') },
          ],
        }] : []),
        ...(user?.can_access_projects ? [{
          title: t('projects') || 'Projects',
          items: [
            { path: '/projects', icon: Icons.projects, label: t('myProjects') },
            { path: '/projects/new', icon: Icons.plus, label: t('createProject') },
            { path: '/claims', icon: Icons.approval, label: language === 'ar' ? 'المستخلصات والطلبات' : 'Claims & Requests' },
          ],
        }] : []),
        ...(user?.can_access_mom ? [{
          title: language === 'ar' ? 'محاضر الاجتماعات' : 'Minutes of Meeting',
          items: [
            { path: '/moms', icon: Icons.mom, label: language === 'ar' ? 'محاضر اجتماعاتي' : 'My MOMs' },
            { path: '/meetings', icon: Icons.mom, label: language === 'ar' ? 'الاجتماعات' : 'Meetings' },
            { path: '/moms/new', icon: Icons.plus, label: language === 'ar' ? 'إنشاء محضر اجتماع' : 'Create MOM' },
            { path: '/moms/approvals', icon: Icons.approval, label: language === 'ar' ? 'موافقات محاضر الاجتماعات' : 'MOM Approvals' },
          ],
        }] : []),
        ...(user?.can_access_documents ? [{
          title: language === 'ar' ? 'المستندات' : 'Documents',
          items: [
            { path: '/documents', icon: Icons.document, label: language === 'ar' ? 'المستندات' : 'My Documents' },
            { path: '/documents/create', icon: Icons.plus, label: language === 'ar' ? 'إنشاء مستند' : 'Create Document' },
            { path: '/documents/templates', icon: Icons.template, label: language === 'ar' ? 'قوالب المستندات' : 'Document Templates' },
            { path: '/documents/templates/create', icon: Icons.plus, label: language === 'ar' ? 'إنشاء قالب مستند' : 'Create Doc Template' },
          ],
        }] : []),
        ...(user?.can_access_proposals ? [{
          title: language === 'ar' ? 'العروض' : 'Proposals',
          items: [
            { path: '/proposals', icon: Icons.document, label: language === 'ar' ? 'عروض الأسعار' : 'Proposals' },
            { path: '/proposals/new', icon: Icons.plus, label: language === 'ar' ? 'إنشاء عرض' : 'Create Proposal' },
            { path: '/proposals/templates', icon: Icons.template, label: language === 'ar' ? 'قوالب العروض' : 'Proposal Templates' },
            { path: '/proposals/designs', icon: Icons.palette, label: language === 'ar' ? 'قوالب التصميم' : 'Design Templates' },
          ],
        }] : []),
        ...(user?.can_access_payments ? [{
          title: language === 'ar' ? 'المدفوعات' : 'Payments',
          items: [
            { path: '/payments', icon: Icons.payment, label: language === 'ar' ? 'المدفوعات' : 'Payments' },
            // PR 5 — verification queue, editors-with-verify get it too.
            ...(user?.can_verify_payments ? [{
              path: '/payments/pending-verification',
              icon: Icons.approval,
              label: language === 'ar' ? 'بانتظار التحقق' : 'Pending Verification',
            }] : []),
          ],
        }] : []),
        ...(user?.can_access_expenses ? [{
          title: language === 'ar' ? 'المصروفات' : 'Expenses',
          items: [
            { path: '/expenses', icon: Icons.payment, label: language === 'ar' ? 'المصروفات' : 'Expenses' },
            { path: '/expenses/new', icon: Icons.plus, label: language === 'ar' ? 'مصروف جديد' : 'New Expense' },
            // PR 8 — AI Import. Plan-gated; only Pro+ users see it.
            ...(user?.can_use_ai_expense_import ? [{
              path: '/expenses/import',
              icon: Icons.upload,
              label: language === 'ar' ? 'استيراد ذكي' : 'AI Import',
            }] : []),
          ],
        }] : []),
        ...(user?.can_access_income ? [{
          title: language === 'ar' ? 'الإيرادات' : 'Income',
          items: [
            { path: '/income', icon: Icons.payment, label: language === 'ar' ? 'الإيرادات' : 'Income' },
            { path: '/income/new', icon: Icons.plus, label: language === 'ar' ? 'إيراد جديد' : 'New Income' },
          ],
        }] : []),
        ...(user?.can_access_invoicing ? [{
          title: language === 'ar' ? 'الفواتير' : 'Invoicing',
          items: [
            { path: '/invoices', icon: Icons.document, label: language === 'ar' ? 'الفواتير' : 'Invoices' },
            { path: '/invoices/new', icon: Icons.plus, label: language === 'ar' ? 'فاتورة جديدة' : 'New Invoice' },
            // Invoice settings live under System Settings now — see
            // components/settings/InvoicingSettingsTab. Sidebar entry
            // removed to avoid two paths to the same form.
          ],
        }] : []),
        ...(user?.can_access_financial_reports ? [{
          title: language === 'ar' ? 'البيانات المالية' : 'Financials',
          items: [
            { path: '/financials', icon: Icons.reports, label: language === 'ar' ? 'لوحة البيانات المالية' : 'Dashboard' },
          ],
        }] : []),
        {
          items: [
            { path: '/notifications', icon: Icons.notifications, label: t('notifications'), badge: notificationCount > 0 ? notificationCount : undefined },
            ...(user?.can_access_reports ? [{ path: '/reports', icon: Icons.reports, label: t('reports') }] : []),
            // Editors can be granted settings access by an admin — honor it.
            ...(user?.can_access_settings ? [{ path: '/settings', icon: Icons.settings, label: t('systemSettings') }] : []),
          ],
        },
      ];
    } else {
      // Client — use per-representative permissions from client_profile_info
      const cpi = user?.client_profile_info;
      return [
        {
          title: t('main') || 'Main',
          items: [
            { path: '/dashboard', icon: Icons.dashboard, label: t('dashboard') },
            { path: '/ai-search', icon: Icons.aiSearch, label: language === 'ar' ? 'البحث الذكي' : 'AI Search' },
            ...(user?.can_access_applications ? [{ path: '/transactions', icon: Icons.transactions, label: t('myTransactions') }] : []),
            ...(user?.can_access_projects ? [{ path: '/projects', icon: Icons.projects, label: t('myProjects') }] : []),
            ...(user?.can_access_projects ? [{ path: '/claims', icon: Icons.approval, label: language === 'ar' ? 'المستخلصات والطلبات' : 'Claims & Requests' }] : []),
          ],
        },
        ...(user?.can_access_mom ? [{
          title: language === 'ar' ? 'محاضر الاجتماعات' : 'Minutes of Meeting',
          items: [
            { path: '/moms', icon: Icons.mom, label: language === 'ar' ? 'محاضر اجتماعاتي' : 'My MOMs' },
            { path: '/meetings', icon: Icons.mom, label: language === 'ar' ? 'الاجتماعات' : 'Meetings' },
            { path: '/moms/approvals', icon: Icons.approval, label: language === 'ar' ? 'موافقات محاضر الاجتماعات' : 'MOM Approvals' },
          ],
        }] : []),
        ...(user?.can_access_documents ? [{
          title: language === 'ar' ? 'المستندات' : 'Documents',
          items: [
            { path: '/documents', icon: Icons.document, label: language === 'ar' ? 'المستندات' : 'My Documents' },
          ],
        }] : []),
        ...(user?.can_access_proposals ? [{
          title: language === 'ar' ? 'العروض' : 'Proposals',
          items: [
            { path: '/proposals', icon: Icons.document, label: language === 'ar' ? 'عروض الأسعار' : 'Proposals' },
          ],
        }] : []),
        ...(user?.can_access_payments ? [{
          title: language === 'ar' ? 'المدفوعات' : 'Payments',
          items: [
            { path: '/payments', icon: Icons.payment, label: language === 'ar' ? 'المدفوعات' : 'Payments' },
            { path: '/my-invoices', icon: Icons.document, label: language === 'ar' ? 'فواتيري' : 'My Invoices' },
          ],
        }] : []),
        {
          items: [
            // Team management — primary representatives only (the API 403s
            // for everyone else, so don't even show the entry).
            ...(cpi?.representative_type === 'primary' ? [{
              path: '/my-team',
              icon: Icons.users,
              label: language === 'ar' ? 'فريقي' : 'My Team',
            }] : []),
            { path: '/notifications', icon: Icons.notifications, label: t('notifications'), badge: notificationCount > 0 ? notificationCount : undefined },
          ],
        },
      ];
    }
  };

  const menuGroups = getMenuGroups();

  // Group containing the current route — always shown expanded (smart
  // expand), without persisting the auto-expansion.
  const activeGroupKey = useMemo(() => {
    const match = menuGroups.find(g =>
      g.items.some(i => location.pathname === i.path || location.pathname.startsWith(i.path + '/'))
    );
    return match ? groupKey(match) : null;
  }, [menuGroups, location.pathname]);

  // Smart expand: manual choice wins for other groups, but the group
  // containing the current page is never hidden.
  const isGroupOpen = (key: string): boolean =>
    (expandedGroups[key] ?? false) || key === activeGroupKey;

  const query = searchQuery.trim().toLowerCase();

  // While searching: groups with no matching items disappear, matches
  // render force-expanded regardless of collapse state.
  const visibleGroups = query
    ? menuGroups
        .map(g => ({ ...g, items: g.items.filter(i => i.label.toLowerCase().includes(query)) }))
        .filter(g => g.items.length > 0)
    : menuGroups;

  return (
    <aside className={classNames('ecme-side-nav', { collapsed })}>
      {/* Logo Header */}
      <div className="ecme-side-nav-header">
        <div className="ecme-side-nav-logo">
          {/* Brand identity only — the system title renders once, centered in
              the header. Tenants without a logo fall back to their name. */}
          {branding.logo_url ? (
            <img
              src={branding.logo_url}
              alt={branding.company_name}
              className="ecme-side-nav-logo-img"
            />
          ) : (
            <span className="ecme-side-nav-logo-text">{branding.company_name}</span>
          )}
        </div>
      </div>

      {/* Menu Search (hidden in icon-only collapsed mode) */}
      {!collapsed && (
        <div className="ecme-side-nav-search">
          <svg className="ecme-side-nav-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            className="ecme-side-nav-search-input"
            placeholder={t('searchMenu')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="ecme-side-nav-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Navigation Content */}
      <div className="ecme-side-nav-content">
        {visibleGroups.length === 0 && (
          <div className="px-4 py-6 text-center text-white/50 text-sm">
            {language === 'ar' ? 'لا توجد نتائج' : 'No matches'}
          </div>
        )}
        {visibleGroups.map((group) => {
          const key = groupKey(group);
          const collapsible = !!group.title && !collapsed && !query;
          const open = !!query || collapsed || !group.title || isGroupOpen(key);
          return (
            <div key={key} className="ecme-side-nav-menu-group">
              {group.title && (
                collapsible ? (
                  <button
                    type="button"
                    className="ecme-side-nav-group-toggle"
                    onClick={() => toggleGroup(key)}
                    aria-expanded={open}
                  >
                    <span className="ecme-side-nav-menu-group-title mb-0">{group.title}</span>
                    <svg
                      className={classNames('ecme-side-nav-group-chevron', { open })}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                ) : (
                  <div className="ecme-side-nav-menu-group-title">
                    {group.title}
                  </div>
                )
              )}
              <div className={classNames('ecme-side-nav-group-items', { open })}>
                <nav className="ecme-side-nav-menu">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end
                      className={({ isActive }) => classNames('ecme-side-nav-menu-item', {
                        active: isActive,
                      })}
                    >
                      <span className="ecme-side-nav-menu-icon">{item.icon}</span>
                      <span className="ecme-side-nav-menu-label">{item.label}</span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className="ecme-side-nav-menu-badge">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </nav>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 text-xs text-white/40 border-t border-white/10">
        Version 1.0.0
      </div>
    </aside>
  );
};

export default SideNav;
